<template>
    <div class="title bg-success">Política de Horário</div>
    <fieldset :disabled="!isEditing.value">
        <div class="row mb-3">
            <div class="col-md-6">
                <label for="name" class="col-form-label">Nome</label>
                <input v-model="schedulePolicy.name" id="name" class="form-control">
            </div>
        </div>
        
        <div class="row mb-3" v-if="isEditing.value">
            <div class="col-md-12">
                <button type="button" class="btn btn-success btn-sm" @click="saveBasicData">
                    Salvar
                </button>
            </div>
        </div>

          
        <div class="row">
            <div class="col-md-12">
                <div class="hours-grid">
                    <div class="grid-header">
                        <div class="hour-label-header">Hora</div>
                        <div v-for="day in daysOfWeek" :key="day.value" class="day-header">
                            {{ day.label.substring(0, 3) }}
                        </div>
                    </div>
                      <div v-for="hour in hours" :key="hour" class="hour-row">
                        <div class="hour-label">{{ formatHour(hour) }}</div>
                        <div v-for="day in daysOfWeek" :key="`${day.value}-${hour}`" 
                             class="hour-cell"
                             :title="`${day.label} - ${formatHour(hour)}`">
                            <div class="minute-container">
                                <div v-for="minute in 60" :key="minute" 
                                     class="minute-cell"
                                     :class="{ 'active': isMinuteActive(day.value, hour, minute-1) }"
                                     :title="getMinuteTooltip(day.value, hour, minute-1)">
                                </div>
                            </div>
                            <div class="hour-number">{{ hour }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
         
        <div class="title subtitle">Intervalos</div>
        <div class="mb-3">
            <button v-if="isEditing.value" class="btn btn-success btn-sm" @click="addInterval">
                Adicionar
            </button>
        </div>

        <table class="w-100 w-full border mt-4">
            <thead>
                <tr class="bg-gray-100 text-left">
                    <th class="border px-4 py-1">Dia da Semana</th>
                    <th class="border px-4 py-1">Início</th>
                    <th class="border px-4 py-1">Fim</th>
                    <th v-if="isEditing.value" class="border px-4 py-1">Ações</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(interval, idx) in intervals" :key="interval.id || idx">
                    <td class="border px-4 py-1">
                        <select v-model="interval.dayOfWeek" class="form-select" :disabled="!isEditing.value">
                            <option v-for="day in daysOfWeek" :key="day.value" :value="day.value">{{ day.label }}</option>
                        </select>
                    </td>

                    <td class="border px-4 py-1">
                        <input v-model="interval.startTime" type="time" class="form-control" :disabled="!isEditing.value">
                    </td>

                    <td class="border px-4 py-1">
                        <input v-model="interval.endTime" type="time" class="form-control" :disabled="!isEditing.value">
                    </td>
                    <td v-if="isEditing.value" class="border px-4 py-1 space-x-2">
                        <div class="d-flex gap-1">
                            <button class="btn btn-success btn-sm" @click="saveInterval(interval, idx)">
                                Salvar
                            </button>
                            <button class="btn btn-danger btn-sm" @click="confirmDeleteInterval(interval, idx)">
                                Excluir
                            </button>
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>

        <ModalContent 
          :is-modal-open="!!deletingInterval" 
          @close="cancelDeleteInterval" 
          @send="executeDeleteInterval" 
          :title="`Excluir Intervalo`">
          Você tem certeza que deseja excluir este intervalo?
        </ModalContent>
    </fieldset>
</template>

<script>
import { CompanyService, schedulePolicyService, scheduleIntervalsService } from "@/services/backend/ApiService";
import useEditMode from "@/services/EditingService";
import ModalContent from '@/components/ModalContent.vue';
import { DayOfWeek } from '../../../../zabbix-shared/interfaces/intefaces';
import { metroCompanyId } from "../../../../zabbix-shared/env";

export default {
    components: {
        ModalContent
    },
    data() {
        return {
            schedulePolicy: {
                id: 0,
                name: '',
                company: null,
                intervals: [],
                createdAt: '',
                updatedAt: '',
                createdBy: null,
                updatedBy: null
            },
            intervals: [],
            selectedCompanyId: '',
            deletingInterval: null,
            daysOfWeek: [
                { label: 'Domingo', value: DayOfWeek.SUNDAY },
                { label: 'Segunda', value: DayOfWeek.MONDAY },
                { label: 'Terça', value: DayOfWeek.TUESDAY },
                { label: 'Quarta', value: DayOfWeek.WEDNESDAY },
                { label: 'Quinta', value: DayOfWeek.THURSDAY },
                { label: 'Sexta', value: DayOfWeek.FRIDAY },
                { label: 'Sábado', value: DayOfWeek.SATURDAY }
            ],
            hours: Array.from({ length: 24 }, (_, i) => i)
        };
    },
    setup() {
        const isEditing = useEditMode();
        return { isEditing };
    },
    methods: {
        formatHour(hour) {
            return `${hour.toString().padStart(2, '0')}:00`;
        },

        isHourActive(dayOfWeek, hour) {
            const dayIntervals = this.intervals.filter(interval => interval.dayOfWeek === dayOfWeek);
            
            for (const interval of dayIntervals) {
                const startHour = this.timeToHour(interval.startTime);
                const endHour = this.timeToHour(interval.endTime);
                
                if (startHour <= endHour) {
                    if (hour >= startHour && hour < endHour) {
                        return true;
                    }
                } else {
                    if (hour >= startHour || hour < endHour) {
                        return true;
                    }
                }
            }
            return false;
        },

        isQuarterActive(dayOfWeek, hour, quarter) {
            const dayIntervals = this.intervals.filter(interval => interval.dayOfWeek === dayOfWeek);
            
            const quarterStartMinutes = hour * 60 + (quarter * 15);
            const quarterEndMinutes = quarterStartMinutes + 15;
            
            for (const interval of dayIntervals) {
                const startMinutes = this.timeToMinutes(interval.startTime);
                const endMinutes = this.timeToMinutes(interval.endTime);
                
                if (startMinutes <= endMinutes) {
                    if (quarterStartMinutes < endMinutes && quarterEndMinutes > startMinutes) {
                        return true;
                    }
                } else {
                    if (quarterStartMinutes < endMinutes || quarterEndMinutes > startMinutes) {
                        return true;
                    }
                }
            }
            return false;
        },

        isMinuteActive(dayOfWeek, hour, minute) {
            const dayIntervals = this.intervals.filter(interval => interval.dayOfWeek === dayOfWeek);
            
            const currentMinutes = hour * 60 + minute;
            
            for (const interval of dayIntervals) {
                const startMinutes = this.timeToMinutes(interval.startTime);
                const endMinutes = this.timeToMinutes(interval.endTime);
                
                if (startMinutes <= endMinutes) {
                    if (currentMinutes >= startMinutes && currentMinutes < endMinutes) {
                        return true;
                    }
                } else {
                    if (currentMinutes >= startMinutes || currentMinutes < endMinutes) {
                        return true;
                    }
                }
            }
            return false;
        },

        timeToHour(timeString) {
            if (!timeString) return 0;
            const [hours] = timeString.split(':').map(Number);
            return hours;
        },

        timeToMinutes(timeString) {
            if (!timeString) return 0;
            const [hours, minutes] = timeString.split(':').map(Number);
            return hours * 60 + minutes;
        },

        getQuarterTooltip(dayOfWeek, hour, quarter) {
            const dayLabel = this.daysOfWeek.find(d => d.value === dayOfWeek)?.label || '';
            const startMinutes = quarter * 15;
            const endMinutes = startMinutes + 14;
            
            const startTime = `${hour.toString().padStart(2, '0')}:${startMinutes.toString().padStart(2, '0')}`;
            const endTime = `${hour.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
            
            const isActive = this.isQuarterActive(dayOfWeek, hour, quarter);
            const status = isActive ? 'ATIVO' : 'INATIVO';
            
            return `${dayLabel} - ${startTime} às ${endTime}\nStatus: ${status}`;
        },

        getMinuteTooltip(dayOfWeek, hour, minute) {
            const dayLabel = this.daysOfWeek.find(d => d.value === dayOfWeek)?.label || '';
            const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
            
            const isActive = this.isMinuteActive(dayOfWeek, hour, minute);
            const status = isActive ? 'ATIVO' : 'INATIVO';
            
            return `${dayLabel} - ${time}\nStatus: ${status}`;
        },

        async saveBasicData() {
            try {
                if (this.selectedCompanyId) {
                    this.schedulePolicy.company = this.companies.find(c => c.id === Number(this.selectedCompanyId));
                }

                if (this.schedulePolicy.id) {
                    await schedulePolicyService.update(this.schedulePolicy.id, this.schedulePolicy);
                } else {
                    const newPolicy = await schedulePolicyService.create(this.schedulePolicy);
                    this.schedulePolicy.id = newPolicy.id;
                }
                
                console.log("Dados básicos salvos com sucesso!");
            } catch (error) {
                console.error("Erro ao salvar dados básicos:", error);
            }
        },

        async saveInterval(interval, index) {
            try {
                interval.schedulePolicy = { id: this.schedulePolicy.id };
                interval.company = this.schedulePolicy.company;

                if (interval.id) {
                    await scheduleIntervalsService.update(interval.id, interval);
                } else {
                    const newInterval = await scheduleIntervalsService.create(interval);
                    this.intervals[index] = newInterval;
                }
                
                console.log("Intervalo salvo com sucesso!");
            } catch (error) {
                console.error("Erro ao salvar intervalo:", error);
            }
        },

        async loadSchedulePolicyData(id) {
            try {
                this.schedulePolicy = await schedulePolicyService.findOne(id);
                this.selectedCompanyId = this.schedulePolicy.company?.id || '';
                
                this.intervals = await scheduleIntervalsService.findAll({
                    schedulePolicy: { id: id }
                });
            } catch (error) {
                console.error("Erro ao carregar os dados da política de horário:", error);
            }
        },

        async loadCompanies() {
            try {
                this.companies = await CompanyService.findAll();
            } catch (error) {
                console.error("Erro ao carregar empresas:", error);
            }
        },

        async saveSchedulePolicy() {
            try {
                if (this.selectedCompanyId) {
                    this.schedulePolicy.company = this.companies.find(c => c.id === Number(this.selectedCompanyId));
                }

                if (this.schedulePolicy.id) {
                    await schedulePolicyService.update(this.schedulePolicy.id, this.schedulePolicy);
                } else {
                    const newPolicy = await schedulePolicyService.create(this.schedulePolicy);
                    this.schedulePolicy.id = newPolicy.id;
                }

                await this.saveIntervals();
                
                this.$router.push({ name: 'SchedulesList' });
            } catch (error) {
                console.error("Erro ao salvar política de horário:", error);
            }
        },

        async saveIntervals() {
            for (const interval of this.intervals) {
                interval.schedulePolicy = { id: this.schedulePolicy.id };
                interval.company = this.schedulePolicy.company;

                if (interval.id) {
                    await scheduleIntervalsService.update(interval.id, interval);
                } else {
                    const newInterval = await scheduleIntervalsService.create(interval);
                    interval.id = newInterval.id;
                }
            }
        },

        async addInterval() {
            let interval = await scheduleIntervalsService.create({
              company: {
                id: metroCompanyId
              },
              schedulePolicy: {id: this.schedulePolicy.id}
            })

            this.intervals.push(interval);
        },

        confirmDeleteInterval(interval, index) {
            this.deletingInterval = { interval, index };
        },

        cancelDeleteInterval() {
            this.deletingInterval = null;
        },

        async executeDeleteInterval() {
            if (this.deletingInterval) {
                await this.removeInterval(this.deletingInterval.index);
                this.deletingInterval = null;
            }
        },

        async removeInterval(index) {
            const interval = this.intervals[index];
            if (interval.id) {
                scheduleIntervalsService.delete(interval.id).then(() => {
                    this.intervals.splice(index, 1);
                }).catch(error => {
                    console.error("Erro ao remover intervalo:", error);
                });
            } else {
                this.intervals.splice(index, 1);
            }
        },

        getIntervalsForDay(day) {
            return this.intervals.filter(interval => interval.dayOfWeek === day);
        },

        editInterval(interval) {
            console.log('Editando intervalo:', interval);
        }
    },
    mounted() {
        const policyId = this.$route.params.id;
        
        this.loadCompanies();
        
        if (policyId && policyId !== 'new') {
            this.loadSchedulePolicyData(policyId);
        }
    }
};
</script>

<style scoped>
.title {
    font-size: 1.2em;
    font-weight: bold;
    margin-bottom: 1em;
    padding: 0.5em;
    border-radius: 0.3em;
}

.subtitle {
    background: #f8f9fa;
    color: #333;
    margin-top: 1.5em;
    margin-bottom: 0.5em;
    padding: 0.5em;
    border-radius: 0.3em;
}

.schedule-calendar {
    background: #f8f9fa;
    padding: 1rem;
    border-radius: 0.5rem;
    border: 1px solid #dee2e6;
}

.day-card {
    background: white;
    border: 1px solid #dee2e6;
    border-radius: 0.375rem;
    padding: 0.75rem;
    margin-bottom: 0.5rem;
    min-height: 120px;
}

.day-title {
    font-weight: 600;
    color: #495057;
    margin-bottom: 0.5rem;
    text-align: center;
    border-bottom: 1px solid #dee2e6;
    padding-bottom: 0.25rem;
}

.time-slot {
    background: #007bff;
    color: white;
    padding: 0.25rem 0.5rem;
    border-radius: 0.25rem;
    margin-bottom: 0.25rem;
    cursor: pointer;
    transition: background-color 0.2s;
}

.time-slot:hover {
    background: #0056b3;
}

.no-time-slot {
    text-align: center;
    padding: 1rem;
    color: #6c757d;
}

option[disabled] {
    background: rgb(221, 221, 221);
}

/* Coluna de ações com largura mínima */
th:last-child,
td:last-child {
    width: 1%;
    white-space: nowrap;
}

/* Estilos para o grid de horas */
.legend {
    margin-bottom: 1rem;
}

.legend-item {
    margin-right: 1rem;
}

.legend-color {
    width: 20px;
    height: 20px;
    border-radius: 3px;
    margin-right: 0.5rem;
    border: 1px solid #dee2e6;
}

.legend-color.active {
    background-color: #007bff;
}

.legend-color.inactive {
    background-color: #f8f9fa;
}

.hours-grid {
    display: grid;
    grid-template-columns: 80px repeat(7, 1fr);
    gap: 1px;
    background-color: #dee2e6;
    border: 1px solid #dee2e6;
    border-radius: 0.375rem;
    overflow: hidden;
}

.grid-header {
    display: contents;
}

.hour-label-header,
.day-header {
    background-color: #e9ecef;
    padding: 0.5rem;
    font-weight: 600;
    text-align: center;
    font-size: 0.875rem;
    color: #495057;
}

.hour-row {
    display: contents;
}

.hour-label {
    background-color: #f8f9fa;
    padding: 0.25rem 0.5rem;
    font-size: 0.75rem;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 500;
    color: #6c757d;
}

.hour-cell {
    background-color: #f8f9fa;
    height: 25px;
    cursor: pointer;
    transition: all 0.2s ease;
    padding: 0;
    overflow: hidden;
}

.hour-cell:hover {
    border-color: #adb5bd;
}

.quarter-container {
    display: flex;
    height: 100%;
    width: 100%;
}

.quarter-cell {
    flex: 1;
    height: 100%;
    background-color: #f8f9fa;
    border-right: 0px solid #e9ecef;
    transition: background-color 0.2s ease;
}

.quarter-cell:last-child {
    border-right: none;
}

.quarter-cell.active {
    background-color: #007bff;
}

.quarter-cell.active:hover,
.hour-cell:hover .quarter-cell.active {
    background-color: #0056b3;
}

.hour-cell:hover .quarter-cell:not(.active) {
    background-color: #e9ecef;
}

.minute-container {
    display: flex;
    height: 100%;
    width: 100%;
}

.minute-cell {
    flex: 1;
    height: 100%;
    background-color: #f8f9fa;
    border-right: 0px solid #e9ecef;
    transition: background-color 0.2s ease;
    min-width: 1px;
    max-width: 3px;
}

.minute-cell:last-child {
    border-right: none;
}

.minute-cell.active {
    background-color: #007bff;
}

.minute-cell.active:hover,
.hour-cell:hover .minute-cell.active {
    background-color: #0056b3;
}

.hour-cell:hover .minute-cell:not(.active) {
    background-color: #e9ecef;
}

/* Responsividade */
@media (max-width: 768px) {
    .hours-grid {
        grid-template-columns: 60px repeat(7, 1fr);
        font-size: 0.75rem;
    }
    
    .hour-label-header,
    .day-header {
        padding: 0.25rem;
        font-size: 0.75rem;
    }
    
    .hour-label {
        font-size: 0.625rem;
        padding: 0.125rem 0.25rem;
    }
    
    .hour-cell {
        height: 20px;
    }
    
    .quarter-cell {
        border-right-width: 0.5px;
    }
}
</style>
